import { useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import ModalBasicLayout from "./ModalBasicLayout";
import ModalCustomReportSavedPrompts from "./ModalCustomReportSavedPrompts";
import ContainerModalActions from "../Containers/ContainerModalActions";
import TextField from "../Form/TextField";
import Select from "../Form/Select";
import RangeDateField from "../Form/RangeDateField";
import LinearLoaderWithTime from "../Loaders/LinearLoaderWithTime";
import { useDispatchApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import useFocusErrorForm from "../../hooks/useFocusErrorForm";
import { useCreativeJobProgressLoader } from "../../hooks/useCreativeJobProgressLoader";
import { startGenerateCustomReport } from "../../actions/customReport";
import { openGeneratedCustomReportFile } from "../../utils/customReportFile";
import { getLanguageFormatted } from "../../utils/string";
import { CREATIVE_JOB_TYPES } from "../../utils/errors";
import { ALERTS_ASYNC_STATUS } from "../../utils/constants";
import {
  BUTTON_CANCEL,
  BUTTON_GENERATE,
  BUTTON_VIEW_REPORT,
  FIELD_REQUIRED,
  FILTER_BY_DATES_PLACEHOLDER,
  MODAL_CUSTOM_REPORT_COLLECTING,
  MODAL_CUSTOM_REPORT_ANALYZING,
  MODAL_CUSTOM_REPORT_DATES,
  MODAL_CUSTOM_REPORT_DESCRIPTION,
  MODAL_CUSTOM_REPORT_ERROR,
  MODAL_CUSTOM_REPORT_FORMAT,
  MODAL_CUSTOM_REPORT_LANGUAGE,
  MODAL_CUSTOM_REPORT_PROMPT,
  MODAL_CUSTOM_REPORT_PROMPT_PLACEHOLDER,
  MODAL_CUSTOM_REPORT_RENDERING,
  MODAL_CUSTOM_REPORT_TEMPLATE,
  MODAL_CUSTOM_REPORT_TITLE,
  MODAL_CUSTOM_REPORT_USE_PREVIOUS_PROMPT,
} from "../../i18n/keysTranslations";
import {
  optionsCustomReportFormat,
  optionsCustomReportLanguage,
  optionsCustomReportTemplate,
} from "../../utils/options";
import { INPUT_COLOR_BORDER } from "../../utils/colors";

const FORMAT_PPTX = "pptx";
const TEMPLATE_CUSTOM = "custom";
const EMPTY_DATE_RANGE = Object.freeze({
  startDateTime: null,
  endDateTime: null,
});

const toFormDate = (value) => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getDefaultForm = (dateRange, defaultLanguage) => ({
  template: "general",
  prompt: "",
  dateRange: {
    startDateTime: dateRange?.startDateTime || null,
    endDateTime: dateRange?.endDateTime || null,
  },
  format: FORMAT_PPTX,
  language: defaultLanguage,
});

const getFormFromGenerationData = (
  generationData = {},
  dateRange = {},
  defaultLanguage
) => ({
  template: generationData.template || "general",
  prompt: String(generationData.customPrompt || generationData.prompt || ""),
  dateRange: {
    startDateTime:
      toFormDate(generationData.startDateTime) ||
      dateRange?.startDateTime ||
      null,
    endDateTime:
      toFormDate(generationData.endDateTime) || dateRange?.endDateTime || null,
  },
  format: generationData.format || FORMAT_PPTX,
  language: generationData.language || defaultLanguage,
});

const ModalCustomReport = ({
  modalOpen = false,
  onCloseModal = () => {},
  dateRange = EMPTY_DATE_RANGE,
  campaignID = "",
  generationData = null,
  jobID = "",
  alertStatus = "",
  alertMessage = "",
}) => {
  const { t, i18n } = useTranslationApp();
  const dispatch = useDispatchApp();
  const defaultLanguage = getLanguageFormatted(i18n.language);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeJobID, setActiveJobID] = useState("");
  const [localGeneratedFile, setLocalGeneratedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSavedPromptsOpen, setIsSavedPromptsOpen] = useState(false);

  const isJobInProgress =
    isSubmitting ||
    alertStatus === ALERTS_ASYNC_STATUS.LOADING ||
    alertStatus === ALERTS_ASYNC_STATUS.GENERATING ||
    (!alertStatus && Boolean(generationData?.isGenerating));
  const generatedFile = generationData?.downloadUrl
    ? {
        downloadUrl: generationData.downloadUrl,
        fileName: generationData.fileName,
        format: generationData.format,
      }
    : localGeneratedFile;
  const hasGeneratedFile = Boolean(generatedFile) && !isJobInProgress;
  const isJobError = alertStatus === ALERTS_ASYNC_STATUS.ERROR;
  const jobCampaignID = generationData?.campaignID || campaignID;

  const { control, handleSubmit, reset, setValue, formState } = useForm({
    defaultValues: getDefaultForm(dateRange, defaultLanguage),
    mode: "onSubmit",
  });
  const { errors } = formState;
  useFocusErrorForm(formState);

  const template = useWatch({ control, name: "template" });
  const watchedDateRange = useWatch({ control, name: "dateRange" });
  const isCustom = template === TEMPLATE_CUSTOM;
  const hasDates = Boolean(
    watchedDateRange?.startDateTime && watchedDateRange?.endDateTime
  );
  const isFormLocked = isSubmitting || isJobInProgress || hasGeneratedFile;
  const storageKey = jobID || activeJobID || "";

  const {
    loaderTimeMs,
    onProgressChange,
    seedProgress,
  } = useCreativeJobProgressLoader({
    storageKey,
    jobType: CREATIVE_JOB_TYPES.generateCustomReport,
    isTerminal: !isJobInProgress,
  });

  const hasHydratedOpenRef = useRef(false);

  useEffect(() => {
    if (!modalOpen) {
      hasHydratedOpenRef.current = false;
      return;
    }
    if (hasHydratedOpenRef.current) {
      return;
    }
    hasHydratedOpenRef.current = true;
    reset(
      generationData
        ? getFormFromGenerationData(generationData, dateRange, defaultLanguage)
        : getDefaultForm(dateRange, defaultLanguage)
    );
    setIsSubmitting(false);
    setActiveJobID("");
    setLocalGeneratedFile(null);
    setErrorMessage("");
    setIsSavedPromptsOpen(false);
  }, [modalOpen, reset, dateRange, generationData, jobID, defaultLanguage]);

  const onSubmit = (values) => {
    if (isSubmitting || isJobInProgress) {
      return;
    }
    if (!values.dateRange?.startDateTime || !values.dateRange?.endDateTime) {
      return;
    }

    const trimmedPrompt = String(values.prompt || "").trim();
    setErrorMessage("");
    setLocalGeneratedFile(null);
    setIsSubmitting(true);

    dispatch(
      startGenerateCustomReport({
        template: values.template,
        format: values.format,
        language: values.language,
        customPrompt:
          values.template === TEMPLATE_CUSTOM ? trimmedPrompt : undefined,
        startDateTime: values.dateRange.startDateTime,
        endDateTime: values.dateRange.endDateTime,
        ...(jobCampaignID ? { campaignID: jobCampaignID } : {}),
      })
    );

    onCloseModal();
  };

  const displayedError =
    errorMessage || (isJobError ? alertMessage || t(MODAL_CUSTOM_REPORT_ERROR) : "");

  return (
    <>
      <ModalBasicLayout
        modalOpen={modalOpen}
        onCloseModal={onCloseModal}
        title={t(MODAL_CUSTOM_REPORT_TITLE)}
        description={t(MODAL_CUSTOM_REPORT_DESCRIPTION)}
        maxWidth="sm"
        fullWidth
      >
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
          }}
        >
          <Select
            control={control}
            name="template"
            label={t(MODAL_CUSTOM_REPORT_TEMPLATE)}
            options={optionsCustomReportTemplate}
            doTranslate
            fullWidth
            variant="filled"
            disabled={isFormLocked}
            errors={errors}
          />

          {isCustom ? (
            <Box>
              <TextField
                fullWidth
                name="prompt"
                label={t(MODAL_CUSTOM_REPORT_PROMPT)}
                placeholder={t(MODAL_CUSTOM_REPORT_PROMPT_PLACEHOLDER)}
                InputLabelProps={{ shrink: true }}
                variant="filled"
                multiline
                minRows={4}
                disabled={isFormLocked}
                control={control}
                errors={errors}
                rules={{
                  required: {
                    value: true,
                    message: t(FIELD_REQUIRED),
                  },
                }}
              />
              {!isJobInProgress ? (
                <Typography
                  component="span"
                  onClick={() => {
                    if (!isSubmitting) {
                      setIsSavedPromptsOpen(true);
                    }
                  }}
                  sx={{
                    textDecoration: "underline",
                    cursor: isSubmitting ? "default" : "pointer",
                    fontSize: 13,
                    color: "primary.main",
                    mt: 0.5,
                    display: "inline-block",
                    "&:hover": { color: "primary.dark" },
                  }}
                >
                  {t(MODAL_CUSTOM_REPORT_USE_PREVIOUS_PROMPT)}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          <Box sx={{ pointerEvents: isFormLocked ? "none" : "auto" }}>
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
              {t(MODAL_CUSTOM_REPORT_DATES)}
            </Typography>
            <RangeDateField
              control={control}
              name="dateRange"
              mode="form"
              errors={errors}
              defaultPlaceholderText={FILTER_BY_DATES_PLACEHOLDER}
              showStartNowButton={false}
              sxField={{
                color: "#000",
                backgroundColor: "fields.background",
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                border: `1px solid ${INPUT_COLOR_BORDER}`,
                "& .MuiInputBase-root": {
                  color: "#000",
                  backgroundColor: "fields.background",
                },
                "& .MuiInputLabel-root": {
                  color: "black !important",
                },
              }}
            />
          </Box>

          <Select
            control={control}
            name="format"
            label={t(MODAL_CUSTOM_REPORT_FORMAT)}
            options={optionsCustomReportFormat}
            doTranslate
            fullWidth
            variant="filled"
            disabled={isFormLocked}
            errors={errors}
          />

          <Select
            control={control}
            name="language"
            label={t(MODAL_CUSTOM_REPORT_LANGUAGE)}
            options={optionsCustomReportLanguage}
            doTranslate
            fullWidth
            variant="filled"
            disabled={isFormLocked}
            errors={errors}
          />

          {displayedError && !isJobInProgress ? (
            <Typography variant="body2" color="error">
              {displayedError}
            </Typography>
          ) : null}
        </Box>
        {isJobInProgress ? (
          <Box sx={{ width: "100%", px: 0, py: 2 }}>
            <LinearLoaderWithTime
              labels={[
                t(MODAL_CUSTOM_REPORT_COLLECTING),
                t(MODAL_CUSTOM_REPORT_ANALYZING),
                t(MODAL_CUSTOM_REPORT_RENDERING),
              ]}
              time={loaderTimeMs}
              progress={seedProgress}
              autoIncrement={true}
              max={99}
              onProgressChange={onProgressChange}
              labelSide="bottom"
              sx={{ width: "100%" }}
            />
          </Box>
        ) : (
          <DialogActions sx={{ p: 0 }}>
            <ContainerModalActions
              hasPadding={true}
              propsSecondaryButton={{
                showButton: !hasGeneratedFile,
                text: BUTTON_CANCEL,
                onClick: onCloseModal,
              }}
              propsPrimaryButton={{
                showButton: true,
                text: hasGeneratedFile ? BUTTON_VIEW_REPORT : BUTTON_GENERATE,
                disabled: hasGeneratedFile
                  ? false
                  : !hasDates || isSubmitting,
                onClick: hasGeneratedFile
                  ? () => openGeneratedCustomReportFile(generatedFile)
                  : handleSubmit(onSubmit),
              }}
            />
          </DialogActions>
        )}
      </ModalBasicLayout>
      <ModalCustomReportSavedPrompts
        modalOpen={isSavedPromptsOpen}
        onCloseModal={() => setIsSavedPromptsOpen(false)}
        onSelectPrompt={(prompt) => {
          setValue("prompt", prompt, {
            shouldValidate: true,
            shouldDirty: true,
          });
          setIsSavedPromptsOpen(false);
        }}
      />
    </>
  );
};

export default ModalCustomReport;
